import React, { useMemo } from 'react';
import { Badge, Card, CardBody, DataTable, Button, Icon, Checkbox } from '@aidkitorg/component-library';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { AdminPanelPage } from '../AdminPanelPage';
import { useLocalizedStrings } from '../../Localization';
import { InvoiceFilters } from './components/filters/InvoiceFilters';
import { useInvoicesContext, Invoice } from '../../Contexts/InvoicesContext';

export const InvoicesPage = () => {
  const L = useLocalizedStrings();
  const { 
    filteredInvoices, 
    isLoading, 
    selectedRows, 
    setFilters, 
    toggleRowSelection, 
    toggleAllRows 
  } = useInvoicesContext();

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.accounting,
    },
    {
      label: L.admin_panel.nav.invoices,
    },
  ];

  const getStatusBadge = (status: Invoice['status']) => {
    switch (status) {
      case 'Unpaid':
        return <Badge variant='error'>{L.admin_panel.invoices.status_unpaid}</Badge>;
      case 'Overdue':
        return <Badge variant='error'>{L.admin_panel.invoices.status_overdue}</Badge>;
      case 'Paid':
        return <Badge variant='success'>{L.admin_panel.invoices.status_paid}</Badge>;
      case 'Partially Paid':
        return <Badge variant='info'>{L.admin_panel.invoices.status_partially_paid}</Badge>;
      case 'Partially Credited':
        return <Badge variant='warning'>{L.admin_panel.invoices.status_partially_credited}</Badge>;
      case 'Pending':
        return <Badge variant='warning'>{L.admin_panel.invoices.status_pending}</Badge>;
      case 'Credited':
        return <Badge variant='info'>{L.admin_panel.invoices.status_credited}</Badge>;
      default:
        return <Badge variant='neutral'>{status}</Badge>;
    }
  };

  const columns: ColumnDef<Invoice>[] = useMemo(() => [
    {
      header: L.admin_panel.invoices.invoice_id.toUpperCase(),
      accessorKey: 'invoice_number',
      cell: ({ row }) => (
        <div>{row.original.invoice_number}</div>
      ),
    },
    {
      header: L.admin_panel.invoices.issue_date.toUpperCase(),
      accessorKey: 'invoice_date',
      cell: ({ row }) => {
        if (!row.original.invoice_date) return '--';
        const date = new Date(row.original.invoice_date);
        if (isNaN(date.getTime())) return '--';
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      header: L.admin_panel.invoices.due_date.toUpperCase(),
      accessorKey: 'due_date',
      cell: ({ row }) => {
        if (!row.original.due_date) return '--';
        const date = new Date(row.original.due_date);
        if (isNaN(date.getTime())) return '--';
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      },
    },
    {
      header: L.admin_panel.invoices.total.toUpperCase(),
      accessorKey: 'total_amount',
      cell: ({ row }) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(row.original.total_amount);
      },
    },
    {
      header: L.admin_panel.invoices.status.toUpperCase(),
      accessorKey: 'status',
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
  ], [selectedRows, filteredInvoices.length, toggleAllRows, toggleRowSelection, L]);

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.invoices}
      breadcrumbs={breadcrumbs}
    >
      <div className="w-full space-y-4">
        {/* Header with invoice count and bulk download button */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {filteredInvoices.length === 1 
              ? L.admin_panel.invoices.invoice_count.replace('{count}', filteredInvoices.length.toString())
              : L.admin_panel.invoices.invoice_count_plural.replace('{count}', filteredInvoices.length.toString())
            }
          </div>
        </div>

        <Card>
          <CardBody>
            <DataTable
              columns={columns}
              data={filteredInvoices}
              showPagination
              pageSize={10}
              initialSorting={[{ id: 'invoice_date', desc: true }]}
              enableSearch
              searchLabel={L.admin_panel.invoices.search_placeholder}
              dataFilteredOutMessage={L.admin_panel.invoices.no_invoices_match_filter}
              loading={isLoading}
              getRowId={(row) => row.id}
              filters={(table) => (
                <InvoiceFilters onFiltersChange={setFilters} />
              )}
            />
          </CardBody>
        </Card>
      </div>
    </AdminPanelPage>
  );
};
